/**
 * RoomCatalog3D - High-Fidelity 3D Room, Table, and Seating Environments for Crazy Cribbage
 * Generates procedural PBR rooms, tables, and radial seating in Babylon.js.
 * Zero external network dependencies: all textures and models are generated procedurally.
 */

export class RoomCatalog3D {
  /**
   * Theme configuration dictionary.
   */
  static ROOM_THEMES = {
    pub: {
      id: 'pub',
      name: 'The Old Pub & Tavern',
      description: 'Oak plank tavern floor, aged brick and wood paneling, brass pendant, and tavern chairs',
      wallColor: '#2b1b11',
      brickColor: '#5c2a1a',
      floorColor: '#1e130a',
      tableFinish: 'oak',
      tableStyle: 'trestle',
      chairStyle: 'pub',
      ceilingStyle: 'beams',
      ambientColor: { r: 0.95, g: 0.75, b: 0.45 },
      lightColor: { r: 1.0, g: 0.82, b: 0.55 },
      lightIntensity: 1.25,
      hasFireplace: true,
      hasCoasters: true
    },
    vegas: {
      id: 'vegas',
      name: 'Vegas High Roller Suite',
      description: 'Polished checkered marble, gold moldings, nighttime skyline view, baize card table',
      wallColor: '#121820',
      brickColor: '#0a1018',
      floorColor: '#0d131a',
      tableFinish: 'baize',
      tableStyle: 'pedestal',
      chairStyle: 'swivel',
      ceilingStyle: 'coffered',
      ambientColor: { r: 0.65, g: 0.75, b: 0.95 },
      lightColor: { r: 0.98, g: 0.95, b: 0.88 },
      lightIntensity: 1.35,
      hasSkyline: true,
      hasChips: true
    },
    cabin: {
      id: 'cabin',
      name: 'Cozy Mountain Lodge',
      description: 'Knotty pine timber logs, stone hearth, snow-capped window, rustic live-edge slab table',
      wallColor: '#3d2514',
      brickColor: '#4a3220',
      floorColor: '#2b190c',
      tableFinish: 'slab',
      tableStyle: 'rustic',
      chairStyle: 'timber',
      ceilingStyle: 'trusses',
      ambientColor: { r: 0.98, g: 0.72, b: 0.40 },
      lightColor: { r: 1.0, g: 0.78, b: 0.48 },
      lightIntensity: 1.2,
      hasFireplace: true,
      hasSnowWindow: true
    },
    velvet: {
      id: 'velvet',
      name: 'Velvet Speakeasy Lounge',
      description: 'Chevron parquet floor, emerald & burgundy tufted velvet walls, art-deco walnut table',
      wallColor: '#14251e',
      brickColor: '#2d121c',
      floorColor: '#1c150e',
      tableFinish: 'walnut',
      tableStyle: 'artdeco',
      chairStyle: 'velvet',
      ceilingStyle: 'tin',
      ambientColor: { r: 0.92, g: 0.68, b: 0.48 },
      lightColor: { r: 1.0, g: 0.85, b: 0.60 },
      lightIntensity: 1.15,
      hasArtDecoTrim: true
    },
    thunderdome: {
      id: 'thunderdome',
      name: 'The Thunderdome / Industrial',
      description: 'Diamond-plate steel flooring, hazard lines, riveted iron walls, exposed copper fixtures',
      wallColor: '#1a1d22',
      brickColor: '#111418',
      floorColor: '#15181e',
      tableFinish: 'steel',
      tableStyle: 'industrial',
      chairStyle: 'bucket',
      ceilingStyle: 'trusses',
      ambientColor: { r: 0.55, g: 0.75, b: 0.95 },
      lightColor: { r: 0.95, g: 0.80, b: 0.50 },
      lightIntensity: 1.3,
      hasHazardStripes: true,
      hasNeonTrim: true
    },
    studio: {
      id: 'studio',
      name: 'Nordic Minimalist Studio',
      description: 'Bleached ash hardwood floor, acoustic oak slat walls, modern round table and shell chairs',
      wallColor: '#2c2e33',
      brickColor: '#222428',
      floorColor: '#3a342c',
      tableFinish: 'ash',
      tableStyle: 'scandinavian',
      chairStyle: 'shell',
      ceilingStyle: 'skylight',
      ambientColor: { r: 0.92, g: 0.94, b: 0.98 },
      lightColor: { r: 1.0, g: 0.98, b: 0.92 },
      lightIntensity: 1.4,
      hasWoodSlats: true
    },
    none: {
      id: 'none',
      name: 'Minimalist Studio Backdrop',
      description: 'Clean studio floor with subtle vignette lighting',
      wallColor: '#181a1f',
      brickColor: '#121418',
      floorColor: '#181a1f',
      tableFinish: 'matte',
      tableStyle: 'pedestal',
      chairStyle: 'shell',
      ceilingStyle: 'flat',
      ambientColor: { r: 0.7, g: 0.7, b: 0.75 },
      lightColor: { r: 1.0, g: 1.0, b: 1.0 },
      lightIntensity: 1.0
    }
  };

  /**
   * Retrieves all available room theme options.
   * @returns {Array<Object>}
   */
  static getThemes() {
    return Object.values(this.ROOM_THEMES);
  }

  /**
   * Retrieves a theme configuration by ID, falling back safely to 'pub'.
   * @param {string} themeId
   * @returns {Object}
   */
  static getThemeById(themeId) {
    if (!themeId || themeId === 'auto') return this.ROOM_THEMES.pub;
    const tid = String(themeId).toLowerCase();
    if (tid === 'raceway') return this.ROOM_THEMES.thunderdome;
    if (tid === 'lounge') return this.ROOM_THEMES.velvet;
    return this.ROOM_THEMES[tid] || this.ROOM_THEMES.pub;
  }

  /**
   * Builds the complete 3D Room, Floor, Walls, Table, Chairs, and Lighting.
   * @param {BABYLON.Scene} scene
   * @param {Object} BABYLON
   * @param {string} themeId
   * @param {Object} [options={}]
   * @returns {Object} { rootNode, tableMesh, chairMeshes, lights, theme, dims }
   */
  static buildRoom(scene, BABYLON, themeId, options = {}) {
    if (!scene || !BABYLON) return null;

    const theme = this.getThemeById(themeId);
    const bounds = options.bounds || { min: { y: -0.8 }, size: { x: 120, z: 80 } };
    const baseBoardX = bounds.size?.x || 120;
    const baseBoardZ = bounds.size?.z || 80;

    // Scale table down to match the 50% scaled cribbage board with natural card and prop margins
    const tableW = Math.max(88, Math.round(baseBoardX + 54.0));
    const tableD = Math.max(72, Math.round(baseBoardZ + 48.0));
    const tableH = 3.6;
    const boardBottomY = bounds.min?.y != null ? bounds.min.y : -0.8;
    const tableSurfaceY = boardBottomY - 0.08;
    const floorY = tableSurfaceY - 110.0; // Scaled clearance to match chair scale and realistic table height

    // Room dimensions scaled to provide ample space for full camera zoom-out and wide mobile views
    const roomW = Math.max(760, Math.round(tableW * 4.8));
    const roomD = Math.max(720, Math.round(tableD * 4.8));
    const roomH = 560.0; // Wall height
    const ceilingY = floorY + roomH;

    const seatW = Math.min(84.0, Math.round(tableW * 0.52));
    const seatD = Math.min(70.0, Math.round(tableD * 0.52));
    const seatH = 10.0;
    const seatElev = 66.0;
    const backH = 52.0;

    const dims = { tableW, tableD, tableH, tableSurfaceY, floorY, roomW, roomD, roomH, ceilingY, seatW, seatD, seatH, seatElev, backH };

    // Parent root node for clean lifecycle management
    const rootNode = new BABYLON.TransformNode(`room3DRoot_${theme.id}`, scene);

    const lights = [];
    const chairMeshes = [];

    // 1. Build Room Shell (Floor, Walls, Ceiling)
    if (theme.id !== 'none') {
      this.buildRoomShell(scene, BABYLON, rootNode, theme, dims);
    } else {
      this.buildMinimalFloor(scene, BABYLON, rootNode, dims);
    }

    // 2. Build 3D Table with legs
    const tableMesh = this.buildTable(scene, BABYLON, rootNode, theme, dims);

    // 3. Build Chairs around the Table
    const seatCount = options.seatCount || (Array.isArray(options.activeTracks) ? options.activeTracks.length : 2);
    const chairs = this.buildChairs(scene, BABYLON, rootNode, theme, dims, Math.max(2, Math.min(8, seatCount)));
    chairMeshes.push(...chairs);

    // 4. Build Thematic Overhead & Ambient Lighting
    const roomLights = this.buildLighting(scene, BABYLON, rootNode, theme, dims, bounds);
    lights.push(...roomLights);

    // 5. Build Thematic 3D Props (Phase 5)
    const props = (theme.id !== 'none') ? this.buildProps(scene, BABYLON, rootNode, theme, dims) : [];

    const chairRoots = chairs.map(c => c.parent).filter(Boolean);

    return {
      rootNode,
      tableMesh,
      chairMeshes,
      chairRoots,
      lights,
      props,
      theme,
      dims
    };
  }

  /**
   * Builds the room shell: Floor plane, Back Wall, Side Walls, and Ceiling with procedural PBR materials.
   */
  static buildRoomShell(scene, BABYLON, rootNode, theme, dims) {
    const { roomW, roomD, floorY, ceilingY, roomH } = dims;

    // A. FLOOR PLANE
    const floor = BABYLON.MeshBuilder.CreateGround('room3DFloor', { width: roomW, height: roomD }, scene);
    floor.position.set(0, floorY, 0);
    floor.parent = rootNode;
    floor.receiveShadows = true;
    floor.isPickable = false;
    floor.checkCollisions = true;

    const floorMat = new BABYLON.PBRMaterial(`matRoomFloor_${theme.id}`, scene);
    floorMat.roughness = theme.id === 'vegas' ? 0.18 : (theme.id === 'pub' ? 0.42 : 0.35);
    floorMat.metallic = theme.id === 'thunderdome' ? 0.65 : 0.05;

    if (BABYLON.DynamicTexture) {
      const floorTex = new BABYLON.DynamicTexture(`texRoomFloor_${theme.id}`, { width: 1024, height: 1024 }, scene, true);
      const ctx = floorTex.getContext();
      this.drawFloorTexture(ctx, theme);
      floorTex.update();
      floorTex.uScale = 4.0;
      floorTex.vScale = 4.0;
      floorMat.albedoTexture = floorTex;
    } else {
      floorMat.albedoColor = new BABYLON.Color3(0.12, 0.08, 0.05);
    }
    floor.material = floorMat;

    // B. BACK WALL (Double Sided)
    const doubleSide = (BABYLON.Mesh && BABYLON.Mesh.DOUBLESIDE != null) ? BABYLON.Mesh.DOUBLESIDE : 2;
    const backWall = BABYLON.MeshBuilder.CreatePlane('room3DBackWall', { width: roomW, height: roomH, sideOrientation: doubleSide }, scene);
    backWall.position.set(0, floorY + roomH / 2, roomD / 2);
    backWall.parent = rootNode;
    backWall.receiveShadows = true;
    backWall.isPickable = false;
    backWall.checkCollisions = true;

    const wallMat = new BABYLON.PBRMaterial(`matRoomWall_${theme.id}`, scene);
    wallMat.roughness = 0.65;
    wallMat.metallic = 0.02;
    wallMat.backFaceCulling = false; // Double-sided wall rendering
    wallMat.twoSidedLighting = true;

    if (BABYLON.DynamicTexture) {
      const wallTex = new BABYLON.DynamicTexture(`texRoomWall_${theme.id}`, { width: 1024, height: 512 }, scene, true);
      const ctx = wallTex.getContext();
      this.drawWallTexture(ctx, theme);
      wallTex.update();
      wallTex.uScale = 2.0;
      wallTex.vScale = 4.0; // Scaled for 560-tall wall
      wallMat.albedoTexture = wallTex;
    } else {
      wallMat.albedoColor = new BABYLON.Color3(0.18, 0.14, 0.12);
    }
    backWall.material = wallMat;

    // C. LEFT WALL (West - Door Wall) (Double Sided)
    const leftWall = BABYLON.MeshBuilder.CreatePlane('room3DLeftWall', { width: roomD, height: roomH, sideOrientation: doubleSide }, scene);
    leftWall.rotation.y = Math.PI / 2;
    leftWall.position.set(-roomW / 2, floorY + roomH / 2, 0);
    leftWall.parent = rootNode;
    leftWall.receiveShadows = true;
    leftWall.isPickable = false;
    leftWall.checkCollisions = true;
    leftWall.material = wallMat;

    // D. RIGHT WALL (East) (Double Sided)
    const rightWall = BABYLON.MeshBuilder.CreatePlane('room3DRightWall', { width: roomD, height: roomH, sideOrientation: doubleSide }, scene);
    rightWall.rotation.y = -Math.PI / 2;
    rightWall.position.set(roomW / 2, floorY + roomH / 2, 0);
    rightWall.parent = rootNode;
    rightWall.receiveShadows = true;
    rightWall.isPickable = false;
    rightWall.checkCollisions = true;
    rightWall.material = wallMat;

    // E. FRONT WALL (South) (Double Sided)
    const frontWall = BABYLON.MeshBuilder.CreatePlane('room3DFrontWall', { width: roomW, height: roomH, sideOrientation: doubleSide }, scene);
    frontWall.rotation.y = Math.PI;
    frontWall.position.set(0, floorY + roomH / 2, -roomD / 2);
    frontWall.parent = rootNode;
    frontWall.receiveShadows = true;
    frontWall.isPickable = false;
    frontWall.checkCollisions = true;
    frontWall.material = wallMat;

    // F. BASEBOARD MOLDINGS
    const baseboardH = 6.0;
    const baseboardD = 1.6;
    const baseboardMat = new BABYLON.PBRMaterial(`matBaseboard_${theme.id}`, scene);
    baseboardMat.albedoColor = new BABYLON.Color3(0.14, 0.09, 0.06);
    baseboardMat.roughness = 0.35;

    // Back baseboard (North)
    const backBaseboard = BABYLON.MeshBuilder.CreateBox('baseboardBack', { width: roomW, height: baseboardH, depth: baseboardD }, scene);
    backBaseboard.position.set(0, floorY + baseboardH / 2, roomD / 2 - baseboardD / 2);
    backBaseboard.parent = rootNode;
    backBaseboard.material = baseboardMat;

    // Front baseboard (South)
    const frontBaseboard = BABYLON.MeshBuilder.CreateBox('baseboardFront', { width: roomW, height: baseboardH, depth: baseboardD }, scene);
    frontBaseboard.position.set(0, floorY + baseboardH / 2, -roomD / 2 + baseboardD / 2);
    frontBaseboard.parent = rootNode;
    frontBaseboard.material = baseboardMat;

    // Left baseboard (West)
    const leftBaseboard = BABYLON.MeshBuilder.CreateBox('baseboardLeft', { width: baseboardD, height: baseboardH, depth: roomD }, scene);
    leftBaseboard.position.set(-roomW / 2 + baseboardD / 2, floorY + baseboardH / 2, 0);
    leftBaseboard.parent = rootNode;
    leftBaseboard.material = baseboardMat;

    // Right baseboard (East)
    const rightBaseboard = BABYLON.MeshBuilder.CreateBox('baseboardRight', { width: baseboardD, height: baseboardH, depth: roomD }, scene);
    rightBaseboard.position.set(roomW / 2 - baseboardD / 2, floorY + baseboardH / 2, 0);
    rightBaseboard.parent = rootNode;
    rightBaseboard.material = baseboardMat;

    // G. ARCHITECTURAL WINDOWS
    // 1. Back Wall Window (North) - centerpiece panoramic scenic window
    this.buildWindow(scene, BABYLON, rootNode, theme, 0, floorY + 220.0, roomD / 2 - 1.2, 0, 'north');
    // 2. Front Wall Window (South)
    this.buildWindow(scene, BABYLON, rootNode, theme, 0, floorY + 220.0, -roomD / 2 + 1.2, Math.PI, 'south');

    // H. ARCHITECTURAL ENTRANCE DOOR (On West Wall - 2x enlarged entrance)
    this.buildDoor(scene, BABYLON, rootNode, theme, -roomW / 2 + 1.2, floorY, 0, Math.PI / 2);

    // I. CEILING WITH EXPOSED BEAMS
    const ceiling = BABYLON.MeshBuilder.CreatePlane('room3DCeiling', { width: roomW, height: roomD, sideOrientation: doubleSide }, scene);
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.set(0, ceilingY, 0);
    ceiling.parent = rootNode;
    ceiling.isPickable = false;
    ceiling.checkCollisions = true;

    const ceilingMat = new BABYLON.PBRMaterial(`matRoomCeiling_${theme.id}`, scene);
    ceilingMat.albedoColor = new BABYLON.Color3(0.08, 0.06, 0.05);
    ceilingMat.roughness = 0.8;
    ceilingMat.backFaceCulling = false;
    ceiling.material = ceilingMat;

    // Timber ceiling beams for rustic themes
    if (theme.ceilingStyle === 'beams' || theme.ceilingStyle === 'trusses') {
      const beamMat = new BABYLON.PBRMaterial(`matBeams_${theme.id}`, scene);
      beamMat.albedoColor = new BABYLON.Color3(0.15, 0.09, 0.05);
      beamMat.roughness = 0.5;

      for (let z = -roomD / 2 + 60; z <= roomD / 2 - 60; z += 100) {
        const beam = BABYLON.MeshBuilder.CreateBox(`ceilingBeam_${z}`, { width: roomW, height: 6.0, depth: 10.0 }, scene);
        beam.position.set(0, ceilingY - 3.0, z);
        beam.parent = rootNode;
        beam.isPickable = false;
        beam.material = beamMat;
      }
    }
  }

  /**
   * Minimalist fallback floor for 'none' theme.
   */
  static buildMinimalFloor(scene, BABYLON, rootNode, dims) {
    const floor = BABYLON.MeshBuilder.CreateGround('room3DFloorMin', { width: dims.roomW, height: dims.roomD }, scene);
    floor.position.set(0, dims.floorY, 0);
    floor.parent = rootNode;
    floor.receiveShadows = true;
    floor.checkCollisions = true;

    const floorMat = new BABYLON.PBRMaterial('matRoomFloorMin', scene);
    floorMat.albedoColor = new BABYLON.Color3(0.10, 0.11, 0.14);
    floorMat.roughness = 0.6;
    floor.material = floorMat;

    // Invisible boundary barriers for 'none' theme so camera is strictly contained within room volume
    const doubleSide = (BABYLON.Mesh && BABYLON.Mesh.DOUBLESIDE != null) ? BABYLON.Mesh.DOUBLESIDE : 2;
    const { roomW, roomD, floorY, roomH, ceilingY } = dims;
    const createBarrier = (name, width, height, posX, posY, posZ, rotY = 0, rotX = 0) => {
      const barrier = BABYLON.MeshBuilder.CreatePlane(name, { width, height, sideOrientation: doubleSide }, scene);
      barrier.position.set(posX, posY, posZ);
      if (rotY) barrier.rotation.y = rotY;
      if (rotX) barrier.rotation.x = rotX;
      barrier.parent = rootNode;
      barrier.isVisible = false;
      barrier.isPickable = false;
      barrier.checkCollisions = true;
      return barrier;
    };

    createBarrier('boundaryNorth', roomW, roomH, 0, floorY + roomH / 2, roomD / 2, 0);
    createBarrier('boundarySouth', roomW, roomH, 0, floorY + roomH / 2, -roomD / 2, Math.PI);
    createBarrier('boundaryWest', roomD, roomH, -roomW / 2, floorY + roomH / 2, 0, Math.PI / 2);
    createBarrier('boundaryEast', roomD, roomH, roomW / 2, floorY + roomH / 2, 0, -Math.PI / 2);
    createBarrier('boundaryCeiling', roomW, roomD, 0, ceilingY, 0, 0, Math.PI / 2);
  }

  /**
   * Builds the realistic 3D Table including tabletop, apron, and 4 sturdy legs reaching the floor.
   */
  static buildTable(scene, BABYLON, rootNode, theme, dims) {
    const { tableW, tableD, tableH, tableSurfaceY, floorY } = dims;
    const tableCenterY = tableSurfaceY - tableH / 2;
    const legHeight = Math.max(10, (tableCenterY - tableH / 2) - floorY);

    // 1. Tabletop
    const isRoundTheme = theme.tableStyle === 'artdeco' || theme.tableStyle === 'scandinavian';
    let tabletop;
    if (isRoundTheme) {
      const radius = Math.max(tableW, tableD) * 0.55;
      tabletop = BABYLON.MeshBuilder.CreateCylinder('board3DTableTop', {
        diameter: radius * 2,
        height: tableH,
        tessellation: 48
      }, scene);
    } else {
      tabletop = BABYLON.MeshBuilder.CreateBox('board3DTableTop', {
        width: tableW,
        height: tableH,
        depth: tableD
      }, scene);
    }

    tabletop.position.set(0, tableCenterY, 0);
    tabletop.parent = rootNode;
    tabletop.receiveShadows = true;
    tabletop.checkCollisions = true;

    // Tabletop procedural PBR Material
    const tableMat = new BABYLON.PBRMaterial(`matTablePBR_${theme.id}`, scene);
    tableMat.roughness = theme.tableFinish === 'baize' ? 0.75 : 0.38;
    tableMat.metallic = theme.tableFinish === 'steel' ? 0.7 : 0.04;

    if (BABYLON.DynamicTexture) {
      const tableTex = new BABYLON.DynamicTexture(`texTablePBR_${theme.id}`, { width: 1024, height: 1024 }, scene, true);
      const ctx = tableTex.getContext();
      this.drawTableTexture(ctx, theme, tableW, tableD);
      tableTex.update();
      tableMat.albedoTexture = tableTex;
    } else {
      tableMat.albedoColor = new BABYLON.Color3(0.25, 0.16, 0.10);
    }
    tabletop.material = tableMat;

    // 2. Table Legs
    const legMat = new BABYLON.PBRMaterial(`matTableLegs_${theme.id}`, scene);
    if (theme.tableFinish === 'steel') {
      legMat.albedoColor = new BABYLON.Color3(0.2, 0.22, 0.25);
      legMat.metallic = 0.8;
      legMat.roughness = 0.3;
    } else if (theme.tableStyle === 'pedestal') {
      legMat.albedoColor = new BABYLON.Color3(0.18, 0.12, 0.08);
      legMat.roughness = 0.35;
    } else {
      legMat.albedoColor = new BABYLON.Color3(0.16, 0.10, 0.06);
      legMat.roughness = 0.45;
    }

    if (theme.tableStyle === 'pedestal') {
      // Pedestal Base (Center column + base plate)
      const pedestal = BABYLON.MeshBuilder.CreateCylinder('tablePedestal', {
        diameterTop: 20,
        diameterBottom: 32,
        height: legHeight,
        tessellation: 32
      }, scene);
      pedestal.position.set(0, floorY + legHeight / 2, 0);
      pedestal.parent = rootNode;
      pedestal.material = legMat;

      const basePlate = BABYLON.MeshBuilder.CreateCylinder('tableBasePlate', {
        diameter: Math.min(tableW, tableD) * 0.55,
        height: 2.4,
        tessellation: 32
      }, scene);
      basePlate.position.set(0, floorY + 1.2, 0);
      basePlate.parent = rootNode;
      basePlate.material = legMat;
    } else {
      // 4 Sturdy Corner Legs scaled for 40% reduced table
      const legThick = theme.tableStyle === 'industrial' ? 7.2 : 6.0;
      const legX = tableW / 2 - legThick - 2.0;
      const legZ = tableD / 2 - legThick - 2.0;
      const legPositions = [
        [-legX, legZ],
        [legX, legZ],
        [-legX, -legZ],
        [legX, -legZ]
      ];

      legPositions.forEach(([lx, lz], idx) => {
        const leg = BABYLON.MeshBuilder.CreateBox(`tableLeg_${idx}`, {
          width: legThick,
          height: legHeight,
          depth: legThick
        }, scene);
        leg.position.set(lx, floorY + legHeight / 2, lz);
        leg.parent = rootNode;
        leg.material = legMat;
        leg.receiveShadows = true;
      });

      // Apron trim under tabletop
      const apronH = 4.0;
      const apronFront = BABYLON.MeshBuilder.CreateBox('tableApronFront', {
        width: tableW - 8,
        height: apronH,
        depth: 2.0
      }, scene);
      apronFront.position.set(0, tableCenterY - tableH / 2 - apronH / 2, legZ);
      apronFront.parent = rootNode;
      apronFront.material = legMat;

      const apronBack = apronFront.clone('tableApronBack');
      apronBack.position.z = -legZ;
      apronBack.parent = rootNode;
    }

    return tabletop;
  }

  /**
   * Builds 3D Chairs arranged radially around the table, facing inward.
   * @param {BABYLON.Scene} scene
   * @param {Object} BABYLON
   * @param {BABYLON.TransformNode} rootNode
   * @param {Object} theme
   * @param {Object} dims
   * @param {number} count - Player seat count (2 to 8)
   * @returns {Array<BABYLON.Mesh>}
   */
  static buildChairs(scene, BABYLON, rootNode, theme, dims, count = 2) {
    const { tableW, tableD, floorY } = dims;
    const chairs = [];

    // Chair PBR materials
    const seatMat = new BABYLON.PBRMaterial(`matChairSeat_${theme.id}`, scene);
    const frameMat = new BABYLON.PBRMaterial(`matChairFrame_${theme.id}`, scene);

    if (theme.chairStyle === 'swivel' || theme.chairStyle === 'bucket') {
      seatMat.albedoColor = new BABYLON.Color3(0.12, 0.12, 0.14); // Padded black leather
      seatMat.roughness = 0.35;
      frameMat.albedoColor = new BABYLON.Color3(0.85, 0.72, 0.35); // Gold/brass frame
      frameMat.metallic = 0.9;
      frameMat.roughness = 0.25;
    } else if (theme.chairStyle === 'velvet') {
      seatMat.albedoColor = new BABYLON.Color3(0.08, 0.32, 0.22); // Emerald velvet
      seatMat.roughness = 0.75;
      frameMat.albedoColor = new BABYLON.Color3(0.82, 0.70, 0.30); // Gold legs
      frameMat.metallic = 0.92;
      frameMat.roughness = 0.2;
    } else {
      seatMat.albedoColor = new BABYLON.Color3(0.24, 0.15, 0.09); // Dark tavern wood
      seatMat.roughness = 0.45;
      frameMat.albedoColor = new BABYLON.Color3(0.18, 0.11, 0.07);
      frameMat.roughness = 0.5;
    }

    // Ergonomic Armchair Dimensions - proportioned so players fit naturally and chair backs are short
    const seatW = dims.seatW ?? 96.0;      // Proportioned armchair width
    const seatD = dims.seatD ?? 80.0;      // Comfortable seating depth
    const seatH = dims.seatH ?? 12.0;      // Cushion thickness
    const seatElev = dims.seatElev ?? 72.0;// Ergonomic distance from floor to seat cushion
    const chairCenterY = floorY + seatElev;
    const backH = dims.backH ?? 58.0;      // Short backrest (reaches avatar shoulder level, never blocking view)
    const backD = 10.0;                    // Backrest thickness

    // Radial distance from table center - tucked neatly around table
    const radiusX = tableW / 2 + seatD / 2 - 12.0;
    const radiusZ = tableD / 2 + seatD / 2 - 12.0;

    // Angular placement around the table
    const angles = [];
    if (count === 2) {
      // 2 Players: South (0) and North (PI)
      angles.push(0, Math.PI);
    } else if (count === 3) {
      // 3 Players: South (0), West (4*PI/3, left of South), East (2*PI/3, right of South)
      angles.push(0, (4 * Math.PI) / 3, (2 * Math.PI) / 3);
    } else if (count === 4) {
      // 4 Players: South, West, North, East (clockwise around table: p1 South, p2 West, p3 North, p4 East)
      angles.push(0, (3 * Math.PI) / 2, Math.PI, Math.PI / 2);
    } else {
      for (let i = 0; i < count; i++) {
        angles.push((2 * Math.PI - (i * 2 * Math.PI) / count) % (2 * Math.PI));
      }
    }

    angles.forEach((angle, idx) => {
      const chairRoot = new BABYLON.TransformNode(`chair_${idx}`, scene);
      chairRoot.parent = rootNode;

      const posX = Math.sin(angle) * radiusX;
      const posZ = -Math.cos(angle) * radiusZ;
      chairRoot.position.set(posX, 0, posZ);
      chairRoot.rotation.y = angle; // Faces inward toward (0, 0, 0)

      // 1. Seat Cushion
      const cushion = BABYLON.MeshBuilder.CreateBox(`chairSeat_${idx}`, {
        width: seatW,
        height: seatH,
        depth: seatD
      }, scene);
      cushion.position.set(0, chairCenterY, 0);
      cushion.parent = chairRoot;
      cushion.material = seatMat;
      cushion.receiveShadows = true;
      chairs.push(cushion);

      // 2. Short Ergonomic Backrest
      const backrest = BABYLON.MeshBuilder.CreateBox(`chairBack_${idx}`, {
        width: seatW,
        height: backH,
        depth: backD
      }, scene);
      backrest.position.set(0, chairCenterY + backH / 2 + seatH / 2, -seatD / 2 + backD / 2);
      backrest.parent = chairRoot;
      backrest.material = seatMat;
      backrest.receiveShadows = true;

      // 3. Padded Armrests (Left and Right)
      const armW = 8.0;
      const armH = 18.0;
      const armD = seatD * 0.82;
      const armY = chairCenterY + seatH / 2 + armH / 2;
      const armXOffset = seatW / 2 - armW / 2;

      [-armXOffset, armXOffset].forEach((ax, aIdx) => {
        const arm = BABYLON.MeshBuilder.CreateBox(`chairArm_${idx}_${aIdx}`, {
          width: armW,
          height: armH,
          depth: armD
        }, scene);
        arm.position.set(ax, armY, 0);
        arm.parent = chairRoot;
        arm.material = seatMat;
        arm.receiveShadows = true;
      });

      // 4. Chair Legs (4 legs extending to floor)
      const legThick = 8.0;
      const legH = seatElev - seatH / 2;
      const cLegX = seatW / 2 - legThick - 1.5;
      const cLegZ = seatD / 2 - legThick - 1.5;
      const legCoords = [
        [-cLegX, cLegZ],
        [cLegX, cLegZ],
        [-cLegX, -cLegZ],
        [cLegX, -cLegZ]
      ];

      legCoords.forEach(([lx, lz], lIdx) => {
        const cLeg = BABYLON.MeshBuilder.CreateBox(`chairLeg_${idx}_${lIdx}`, {
          width: legThick,
          height: legH,
          depth: legThick
        }, scene);
        cLeg.position.set(lx, floorY + legH / 2, lz);
        cLeg.parent = chairRoot;
        cLeg.material = frameMat;
      });
    });

    return chairs;
  }

  /**
   * Builds realistic overhead pendant/chandelier lighting and warm ambient fills.
   */
  static buildLighting(scene, BABYLON, rootNode, theme, dims, bounds = {}) {
    const { tableSurfaceY, ceilingY, roomH } = dims;
    const lights = [];

    // 1. Overhead Pendant Fixture Mesh - Elevated clearance for tall 3D boards
    const boardHeight = (bounds && bounds.size && bounds.size.y) ? bounds.size.y : 15;
    const fixtureY = Math.max(tableSurfaceY + 75.0, tableSurfaceY + boardHeight + 35.0);
    const cordH = Math.max(10, ceilingY - fixtureY);

    const cord = BABYLON.MeshBuilder.CreateCylinder('lightCord', {
      diameter: 0.8,
      height: cordH,
      tessellation: 12
    }, scene);
    cord.position.set(0, ceilingY - cordH / 2, 0);
    cord.parent = rootNode;

    const shade = BABYLON.MeshBuilder.CreateCylinder('lightShade', {
      diameterTop: 6.0,
      diameterBottom: 22.0,
      height: 9.0,
      tessellation: 24
    }, scene);
    shade.position.set(0, fixtureY, 0);
    shade.parent = rootNode;

    const fixtureMat = new BABYLON.PBRMaterial('matFixture', scene);
    fixtureMat.albedoColor = new BABYLON.Color3(0.22, 0.18, 0.12);
    fixtureMat.metallic = 0.85;
    fixtureMat.roughness = 0.3;
    cord.material = fixtureMat;
    shade.material = fixtureMat;

    // Glowing bulb inside shade
    const bulb = BABYLON.MeshBuilder.CreateSphere('lightBulb', { diameter: 4.5 }, scene);
    bulb.position.set(0, fixtureY - 2.5, 0);
    bulb.parent = rootNode;

    const bulbMat = new BABYLON.PBRMaterial('matBulb', scene);
    bulbMat.emissiveColor = new BABYLON.Color3(theme.lightColor.r, theme.lightColor.g, theme.lightColor.b);
    bulbMat.emissiveIntensity = 2.0;
    bulb.material = bulbMat;

    // 2. Downward SpotLight on the Table & Cribbage Board
    const spot = new BABYLON.SpotLight(
      'roomTableSpot',
      new BABYLON.Vector3(0, fixtureY - 3.0, 0),
      new BABYLON.Vector3(0, -1, 0),
      Math.PI / 2.2, // Angle
      2.0,           // Exponent / falloff
      scene
    );
    spot.diffuse = new BABYLON.Color3(theme.lightColor.r, theme.lightColor.g, theme.lightColor.b);
    spot.intensity = theme.lightIntensity * 2.8;
    spot.range = Math.max(600, roomH);
    spot.parent = rootNode;
    lights.push(spot);

    // 3. Warm Ambient Fill Point Light
    const fillPoint = new BABYLON.PointLight(
      'roomAmbientFill',
      new BABYLON.Vector3(0, tableSurfaceY + 28.0, 0),
      scene
    );
    fillPoint.diffuse = new BABYLON.Color3(theme.ambientColor.r, theme.ambientColor.g, theme.ambientColor.b);
    fillPoint.intensity = theme.lightIntensity * 0.9;
    fillPoint.range = Math.max(850, roomH * 1.5);
    fillPoint.parent = rootNode;
    lights.push(fillPoint);

    return lights;
  }

  /**
   * Procedural texture generator for room flooring.
   */
  static drawFloorTexture(ctx, theme) {
    if (theme.id === 'vegas') {
      // Checkered marble
      for (let x = 0; x < 1024; x += 128) {
        for (let y = 0; y < 1024; y += 128) {
          const isEven = (Math.floor(x / 128) + Math.floor(y / 128)) % 2 === 0;
          ctx.fillStyle = isEven ? '#0a1610' : '#14221a';
          ctx.fillRect(x, y, 128, 128);
          ctx.strokeStyle = '#22382c';
          ctx.lineWidth = 2;
          ctx.strokeRect(x, y, 128, 128);
        }
      }
    } else if (theme.id === 'velvet') {
      // Chevron French parquet
      ctx.fillStyle = '#1e140c';
      ctx.fillRect(0, 0, 1024, 1024);
      for (let y = 0; y < 1024; y += 64) {
        ctx.strokeStyle = '#2d1f14';
        ctx.lineWidth = 3;
        ctx.beginPath();
        for (let x = 0; x <= 1024; x += 128) {
          ctx.lineTo(x, y);
          ctx.lineTo(x + 64, y + 32);
        }
        ctx.stroke();
      }
    } else if (theme.id === 'thunderdome') {
      // Diamond plate steel
      ctx.fillStyle = '#181c22';
      ctx.fillRect(0, 0, 1024, 1024);
      ctx.strokeStyle = '#2c333d';
      ctx.lineWidth = 3;
      for (let x = 0; x < 1024; x += 32) {
        for (let y = 0; y < 1024; y += 32) {
          ctx.beginPath();
          ctx.moveTo(x + 4, y + 4);
          ctx.lineTo(x + 16, y + 16);
          ctx.stroke();
        }
      }
    } else {
      // Oak / Pine wood planks (Pub, Cabin, Studio)
      const baseCol = theme.id === 'studio' ? '#3e3830' : (theme.id === 'cabin' ? '#2e1c0f' : '#22150c');
      const stripeCol = theme.id === 'studio' ? '#363028' : (theme.id === 'cabin' ? '#24150a' : '#1c1008');
      ctx.fillStyle = baseCol;
      ctx.fillRect(0, 0, 1024, 1024);

      for (let y = 0; y < 1024; y += 128) {
        ctx.fillStyle = (Math.floor(y / 128) % 2 === 0) ? baseCol : stripeCol;
        ctx.fillRect(0, y, 1024, 126);
        ctx.fillStyle = '#0f0804';
        ctx.fillRect(0, y + 126, 1024, 2);

        // Wood grain
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.035)';
        ctx.lineWidth = 1;
        for (let i = 0; i < 4; i++) {
          ctx.beginPath();
          ctx.moveTo(0, y + 20 + i * 26);
          ctx.bezierCurveTo(340, y + 25 + i * 26, 680, y + 15 + i * 26, 1024, y + 22 + i * 26);
          ctx.stroke();
        }
      }
    }
  }

  /**
   * Procedural texture generator for room walls.
   */
  static drawWallTexture(ctx, theme) {
    ctx.fillStyle = theme.wallColor || '#22150c';
    ctx.fillRect(0, 0, 1024, 512);

    if (theme.id === 'pub' || theme.hasFireplace) {
      // Lower wainscoting paneling
      ctx.fillStyle = '#1c0f08';
      ctx.fillRect(0, 256, 1024, 256);
      // Paneling vertical stiles
      ctx.strokeStyle = '#2d1a0e';
      ctx.lineWidth = 4;
      for (let x = 0; x < 1024; x += 128) {
        ctx.strokeRect(x + 10, 266, 108, 236);
      }
      // Upper brickwork
      ctx.fillStyle = '#421a10';
      ctx.fillRect(0, 0, 1024, 256);
      ctx.strokeStyle = '#2b1008';
      ctx.lineWidth = 2;
      for (let y = 0; y < 256; y += 24) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(1024, y);
        ctx.stroke();
        const offset = (Math.floor(y / 24) % 2) * 32;
        for (let x = offset; x < 1024; x += 64) {
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x, y + 24);
          ctx.stroke();
        }
      }
    } else if (theme.id === 'vegas') {
      // Luxury damask wallpaper & gold trim
      ctx.fillStyle = '#0e141a';
      ctx.fillRect(0, 0, 1024, 512);
      ctx.strokeStyle = '#c9a44b';
      ctx.lineWidth = 2;
      for (let x = 64; x < 1024; x += 128) {
        ctx.strokeRect(x, 40, 96, 432);
      }
    } else if (theme.id === 'velvet') {
      // Tufted velvet diamonds
      ctx.fillStyle = '#0f241a';
      ctx.fillRect(0, 0, 1024, 512);
      ctx.strokeStyle = '#183829';
      ctx.lineWidth = 2;
      for (let x = 0; x < 1024; x += 64) {
        for (let y = 0; y < 512; y += 64) {
          ctx.beginPath();
          ctx.moveTo(x + 32, y);
          ctx.lineTo(x + 64, y + 32);
          ctx.lineTo(x + 32, y + 64);
          ctx.lineTo(x, y + 32);
          ctx.closePath();
          ctx.stroke();
        }
      }
    } else if (theme.id === 'thunderdome') {
      // Industrial riveted iron bulkheads
      ctx.fillStyle = '#16191f';
      ctx.fillRect(0, 0, 1024, 512);
      ctx.strokeStyle = '#252b34';
      ctx.lineWidth = 3;
      for (let x = 0; x < 1024; x += 256) {
        ctx.strokeRect(x + 6, 6, 244, 500);
      }
    } else {
      // Nordic Studio acoustic slats
      ctx.fillStyle = '#2c2e33';
      ctx.fillRect(0, 0, 1024, 512);
      ctx.fillStyle = '#6e563b'; // Oak slats
      for (let x = 0; x < 1024; x += 24) {
        ctx.fillRect(x, 0, 14, 512);
      }
    }
  }

  /**
   * Procedural texture generator for table surface.
   */
  static drawTableTexture(ctx, theme, tableW, tableD) {
    if (theme.tableFinish === 'baize') {
      // Vegas casino felt baize with armrest rim
      ctx.fillStyle = '#083818';
      ctx.fillRect(0, 0, 1024, 1024);
      // Padded leather elbow rail border
      ctx.fillStyle = '#0e0e12';
      ctx.fillRect(0, 0, 1024, 64);
      ctx.fillRect(0, 960, 1024, 64);
      ctx.fillRect(0, 0, 64, 1024);
      ctx.fillRect(960, 0, 64, 1024);
      // Gold seam line
      ctx.strokeStyle = '#d4af37';
      ctx.lineWidth = 3;
      ctx.strokeRect(66, 66, 892, 892);
    } else if (theme.tableFinish === 'steel') {
      // Steel tabletop with caution lines
      ctx.fillStyle = '#22262e';
      ctx.fillRect(0, 0, 1024, 1024);
      // Hazard perimeter stripes
      ctx.fillStyle = '#d97706';
      ctx.fillRect(0, 0, 1024, 48);
      ctx.fillRect(0, 976, 1024, 48);
      ctx.fillStyle = '#111827';
      for (let x = 0; x < 1024; x += 64) {
        ctx.beginPath();
        ctx.moveTo(x, 0); ctx.lineTo(x + 32, 48); ctx.lineTo(x + 16, 48); ctx.lineTo(x - 16, 0); ctx.fill();
        ctx.beginPath();
        ctx.moveTo(x, 976); ctx.lineTo(x + 32, 1024); ctx.lineTo(x + 16, 1024); ctx.lineTo(x - 16, 976); ctx.fill();
      }
    } else {
      // Fine wood tabletop (Pub oak, Cabin pine, Velvet walnut, Studio ash)
      const woodBase = theme.tableFinish === 'ash' ? '#443c32' : (theme.tableFinish === 'walnut' ? '#26170d' : '#28170c');
      ctx.fillStyle = woodBase;
      ctx.fillRect(0, 0, 1024, 1024);
      // Grain planks
      for (let y = 0; y < 1024; y += 128) {
        ctx.fillStyle = (Math.floor(y / 128) % 2 === 0) ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.03)';
        ctx.fillRect(0, y, 1024, 126);
        ctx.fillStyle = 'rgba(0,0,0,0.3)';
        ctx.fillRect(0, y + 126, 1024, 2);
      }
      // Pub coasters
      if (theme.hasCoasters) {
        ctx.fillStyle = '#f4ead4';
        ctx.beginPath();
        ctx.arc(180, 200, 70, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#b45309';
        ctx.lineWidth = 4;
        ctx.stroke();
        ctx.fillStyle = '#b45309';
        ctx.font = 'bold 20px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('CRIBBAGE', 180, 195);
        ctx.fillText('ALE', 180, 218);

        // Second coaster
        ctx.fillStyle = '#f4ead4';
        ctx.beginPath();
        ctx.arc(840, 820, 70, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.fillText('PINTS', 840, 815);
        ctx.fillText('& PEGS', 840, 838);
      }
    }
  }

  /**
   * Builds an architectural window featuring molded frame casing, sill, muntin grid,
   * glass pane, and scenic dynamic backdrop view.
   */
  static buildWindow(scene, BABYLON, rootNode, theme, posX, posY, posZ, rotY = 0, nameSuffix = '0') {
    const winW = 160.0; // 2x bigger (was 86.0)
    const winH = 200.0; // 2x bigger (was 104.0)
    const frameDepth = 4.8;

    const winRoot = new BABYLON.TransformNode(`windowRoot_${nameSuffix}`, scene);
    winRoot.position.set(posX, posY, posZ);
    winRoot.rotation.y = rotY;
    winRoot.parent = rootNode;

    const frameMat = new BABYLON.PBRMaterial(`matWinFrame_${nameSuffix}_${theme.id}`, scene);
    frameMat.albedoColor = new BABYLON.Color3(0.18, 0.12, 0.08);
    frameMat.roughness = 0.4;
    frameMat.metallic = theme.id === 'vegas' ? 0.6 : 0.05;

    // 1. Outer Frame (Top, Bottom Sill, Left, Right casings)
    const casingThick = 6.0;
    const topCasing = BABYLON.MeshBuilder.CreateBox(`winTop_${nameSuffix}`, { width: winW + casingThick * 2, height: casingThick, depth: frameDepth }, scene);
    topCasing.position.set(0, winH / 2 + casingThick / 2, 0);
    topCasing.parent = winRoot;
    topCasing.material = frameMat;

    const sill = BABYLON.MeshBuilder.CreateBox(`winSill_${nameSuffix}`, { width: winW + casingThick * 2 + 8.0, height: casingThick + 2.0, depth: frameDepth + 4.0 }, scene);
    sill.position.set(0, -winH / 2 - (casingThick + 2.0) / 2, 1.8);
    sill.parent = winRoot;
    sill.material = frameMat;

    const leftCasing = BABYLON.MeshBuilder.CreateBox(`winLeft_${nameSuffix}`, { width: casingThick, height: winH, depth: frameDepth }, scene);
    leftCasing.position.set(-winW / 2 - casingThick / 2, 0, 0);
    leftCasing.parent = winRoot;
    leftCasing.material = frameMat;

    const rightCasing = BABYLON.MeshBuilder.CreateBox(`winRight_${nameSuffix}`, { width: casingThick, height: winH, depth: frameDepth }, scene);
    rightCasing.position.set(winW / 2 + casingThick / 2, 0, 0);
    rightCasing.parent = winRoot;
    rightCasing.material = frameMat;

    // 2. Muntin Grid (Crossbars dividing into 4 panes)
    const barThick = 3.6;
    const horizBar = BABYLON.MeshBuilder.CreateBox(`winMuntinH_${nameSuffix}`, { width: winW, height: barThick, depth: frameDepth * 0.7 }, scene);
    horizBar.position.set(0, 0, 0);
    horizBar.parent = winRoot;
    horizBar.material = frameMat;

    const vertBar = BABYLON.MeshBuilder.CreateBox(`winMuntinV_${nameSuffix}`, { width: barThick, height: winH, depth: frameDepth * 0.7 }, scene);
    vertBar.position.set(0, 0, 0);
    vertBar.parent = winRoot;
    vertBar.material = frameMat;

    // 3. Scenic Outdoor Vista Pane
    const doubleSide = (BABYLON.Mesh && BABYLON.Mesh.DOUBLESIDE != null) ? BABYLON.Mesh.DOUBLESIDE : 2;
    const scenery = BABYLON.MeshBuilder.CreatePlane(`winScenery_${nameSuffix}`, { width: winW, height: winH, sideOrientation: doubleSide }, scene);
    scenery.position.set(0, 0, -0.6);
    scenery.parent = winRoot;

    const sceneryMat = new BABYLON.PBRMaterial(`matWinScenery_${nameSuffix}_${theme.id}`, scene);
    sceneryMat.roughness = 0.2;
    sceneryMat.backFaceCulling = false;

    if (BABYLON.DynamicTexture) {
      const sceneTex = new BABYLON.DynamicTexture(`texWinScenery_${nameSuffix}_${theme.id}`, { width: 512, height: 512 }, scene, true);
      const sCtx = sceneTex.getContext();
      this.drawWindowScenery(sCtx, theme);
      sceneTex.update();
      sceneryMat.albedoTexture = sceneTex;
      sceneryMat.emissiveTexture = sceneTex;
      sceneryMat.emissiveColor = new BABYLON.Color3(0.65, 0.65, 0.65);
    } else {
      sceneryMat.emissiveColor = new BABYLON.Color3(0.12, 0.18, 0.32);
    }
    scenery.material = sceneryMat;

    // 4. Glass Reflection Overlay Pane
    const glass = BABYLON.MeshBuilder.CreatePlane(`winGlass_${nameSuffix}`, { width: winW, height: winH, sideOrientation: doubleSide }, scene);
    glass.position.set(0, 0, 0.4);
    glass.parent = winRoot;

    const glassMat = new BABYLON.PBRMaterial(`matWinGlass_${nameSuffix}`, scene);
    glassMat.alpha = 0.35;
    glassMat.roughness = 0.08;
    glassMat.metallic = 0.2;
    glassMat.backFaceCulling = false;
    glassMat.albedoColor = new BABYLON.Color3(0.85, 0.92, 0.98);
    glass.material = glassMat;

    return winRoot;
  }

  /**
   * Builds an architectural entrance door with casing architrave, molded panels,
   * threshold, and polished brass handle.
   */
  static buildDoor(scene, BABYLON, rootNode, theme, posX, floorY, posZ, rotY = 0) {
    const doorW = 120.0; // 2x bigger (was 66.0)
    const doorH = 340.0; // 2x bigger (was 176.0)
    const frameDepth = 5.0;
    const casingThick = 6.5;

    const doorRoot = new BABYLON.TransformNode('doorRoot_0', scene);
    doorRoot.position.set(posX, floorY, posZ);
    doorRoot.rotation.y = rotY;
    doorRoot.parent = rootNode;

    const casingMat = new BABYLON.PBRMaterial(`matDoorCasing_${theme.id}`, scene);
    casingMat.albedoColor = new BABYLON.Color3(0.14, 0.09, 0.06);
    casingMat.roughness = 0.4;

    const leafMat = new BABYLON.PBRMaterial(`matDoorLeaf_${theme.id}`, scene);
    leafMat.albedoColor = theme.id === 'vegas' ? new BABYLON.Color3(0.10, 0.10, 0.12) : new BABYLON.Color3(0.22, 0.14, 0.08);
    leafMat.roughness = 0.35;
    leafMat.metallic = theme.id === 'thunderdome' ? 0.7 : 0.04;

    // 1. Door Jamb / Architrave Frame
    const topCasing = BABYLON.MeshBuilder.CreateBox('doorTopCasing', { width: doorW + casingThick * 2, height: casingThick, depth: frameDepth }, scene);
    topCasing.position.set(0, doorH + casingThick / 2, 0);
    topCasing.parent = doorRoot;
    topCasing.material = casingMat;

    const leftCasing = BABYLON.MeshBuilder.CreateBox('doorLeftCasing', { width: casingThick, height: doorH, depth: frameDepth }, scene);
    leftCasing.position.set(-doorW / 2 - casingThick / 2, doorH / 2, 0);
    leftCasing.parent = doorRoot;
    leftCasing.material = casingMat;

    const rightCasing = BABYLON.MeshBuilder.CreateBox('doorRightCasing', { width: casingThick, height: doorH, depth: frameDepth }, scene);
    rightCasing.position.set(doorW / 2 + casingThick / 2, doorH / 2, 0);
    rightCasing.parent = doorRoot;
    rightCasing.material = casingMat;

    // Threshold Plate on Floor
    const threshold = BABYLON.MeshBuilder.CreateBox('doorThreshold', { width: doorW + casingThick * 2, height: 2.2, depth: frameDepth + 2.4 }, scene);
    threshold.position.set(0, 1.1, 1.2);
    threshold.parent = doorRoot;
    threshold.material = casingMat;

    // 2. Door Leaf / Slab
    const leaf = BABYLON.MeshBuilder.CreateBox('doorLeaf', { width: doorW, height: doorH, depth: 3.6 }, scene);
    leaf.position.set(0, doorH / 2, 0);
    leaf.parent = doorRoot;
    leaf.material = leafMat;
    leaf.receiveShadows = true;

    // 3. Molded Door Panels
    const panelMat = new BABYLON.PBRMaterial(`matDoorPanel_${theme.id}`, scene);
    panelMat.albedoColor = theme.id === 'vegas' ? new BABYLON.Color3(0.06, 0.06, 0.08) : new BABYLON.Color3(0.16, 0.10, 0.06);
    panelMat.roughness = 0.5;

    const upperPanel = BABYLON.MeshBuilder.CreateBox('doorPanelUpper', { width: doorW * 0.72, height: doorH * 0.40, depth: 1.0 }, scene);
    upperPanel.position.set(0, doorH * 0.70, 2.0);
    upperPanel.parent = doorRoot;
    upperPanel.material = panelMat;

    const lowerPanel = BABYLON.MeshBuilder.CreateBox('doorPanelLower', { width: doorW * 0.72, height: doorH * 0.32, depth: 1.0 }, scene);
    lowerPanel.position.set(0, doorH * 0.28, 2.0);
    lowerPanel.parent = doorRoot;
    lowerPanel.material = panelMat;

    // 4. Polished Brass Door Knob & Escutcheon
    const handleMat = new BABYLON.PBRMaterial('matDoorHandle', scene);
    handleMat.albedoColor = new BABYLON.Color3(0.88, 0.74, 0.35);
    handleMat.metallic = 0.95;
    handleMat.roughness = 0.18;

    const knobEscutcheon = BABYLON.MeshBuilder.CreateCylinder('doorEscutcheon', { diameter: 7.2, height: 0.8, tessellation: 20 }, scene);
    knobEscutcheon.rotation.x = Math.PI / 2;
    knobEscutcheon.position.set(doorW / 2 - 14.0, doorH * 0.48, 2.0);
    knobEscutcheon.parent = doorRoot;
    knobEscutcheon.material = handleMat;

    const knob = BABYLON.MeshBuilder.CreateSphere('doorKnob', { diameter: 5.6 }, scene);
    knob.position.set(doorW / 2 - 14.0, doorH * 0.48, 4.8);
    knob.parent = doorRoot;
    knob.material = handleMat;

    return doorRoot;
  }

  /**
   * Procedural texture generator for scenic outdoor view behind window panes.
   */
  static drawWindowScenery(ctx, theme) {
    if (!ctx) return;
    const w = 512;
    const h = 512;

    if (theme.id === 'vegas') {
      // 1. VEGAS: Neon city skyline at dusk
      const skyGrad = ctx.createLinearGradient(0, 0, 0, h);
      skyGrad.addColorStop(0, '#100624');
      skyGrad.addColorStop(0.65, '#2e0f48');
      skyGrad.addColorStop(1, '#66164d');
      ctx.fillStyle = skyGrad;
      ctx.fillRect(0, 0, w, h);

      ctx.fillStyle = '#ffffff';
      for (let i = 0; i < 40; i++) {
        const sx = (i * 97) % w;
        const sy = (i * 61) % (h * 0.5);
        ctx.fillRect(sx, sy, 2, 2);
      }

      const bldgs = [
        [30, 260, 60, 252, '#0c051a', '#ff007f'],
        [100, 200, 75, 312, '#150826', '#00f0ff'],
        [185, 230, 80, 282, '#0c051a', '#ffd700'],
        [275, 170, 90, 342, '#180a2c', '#ff00aa'],
        [375, 220, 65, 292, '#0c051a', '#00ff88'],
        [450, 250, 55, 262, '#140722', '#ff5500']
      ];
      bldgs.forEach(([bx, by, bw, bh, col, neon]) => {
        ctx.fillStyle = col;
        ctx.fillRect(bx, by, bw, bh);
        ctx.fillStyle = neon;
        ctx.fillRect(bx + 4, by, bw - 8, 3);
        for (let wy = by + 12; wy < by + bh - 10; wy += 14) {
          for (let wx = bx + 8; wx < bx + bw - 8; wx += 12) {
            ctx.fillStyle = (Math.sin(wx * wy) > 0.1) ? neon : 'rgba(255, 255, 200, 0.4)';
            ctx.fillRect(wx, wy, 4, 6);
          }
        }
      });
    } else if (theme.id === 'cabin') {
      // 2. CABIN: Alpine mountain night & starry pine forest
      const skyGrad = ctx.createLinearGradient(0, 0, 0, h);
      skyGrad.addColorStop(0, '#040b18');
      skyGrad.addColorStop(0.7, '#0b1d38');
      skyGrad.addColorStop(1, '#1e3355');
      ctx.fillStyle = skyGrad;
      ctx.fillRect(0, 0, w, h);

      ctx.fillStyle = '#fffae0';
      ctx.beginPath();
      ctx.arc(380, 90, 32, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#040b18';
      ctx.beginPath();
      ctx.arc(370, 85, 28, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#101c2e';
      ctx.beginPath();
      ctx.moveTo(0, 340);
      ctx.lineTo(140, 240);
      ctx.lineTo(260, 310);
      ctx.lineTo(390, 220);
      ctx.lineTo(512, 330);
      ctx.lineTo(512, 512);
      ctx.lineTo(0, 512);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#c8daf0';
      ctx.beginPath();
      ctx.moveTo(140, 240);
      ctx.lineTo(120, 270);
      ctx.lineTo(160, 270);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(390, 220);
      ctx.lineTo(365, 260);
      ctx.lineTo(415, 260);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#050a12';
      for (let px = 10; px < w; px += 45) {
        const py = 330 + (px % 40);
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(px - 22, py + 160);
        ctx.lineTo(px + 22, py + 160);
        ctx.closePath();
        ctx.fill();
      }
    } else if (theme.id === 'pub') {
      // 3. PUB: Rainy Victorian street & warm tavern lantern glow
      const skyGrad = ctx.createLinearGradient(0, 0, 0, h);
      skyGrad.addColorStop(0, '#0c121e');
      skyGrad.addColorStop(0.7, '#1b2432');
      skyGrad.addColorStop(1, '#2c3325');
      ctx.fillStyle = skyGrad;
      ctx.fillRect(0, 0, w, h);

      ctx.fillStyle = '#12161c';
      ctx.fillRect(20, 180, 150, 332);
      ctx.fillRect(180, 220, 170, 292);
      ctx.fillRect(360, 160, 140, 352);

      ctx.fillStyle = '#0a0d12';
      ctx.fillRect(60, 155, 24, 25);
      ctx.fillRect(410, 135, 24, 25);

      const lanternGlow = ctx.createRadialGradient(260, 380, 10, 260, 380, 130);
      lanternGlow.addColorStop(0, 'rgba(255, 195, 77, 0.85)');
      lanternGlow.addColorStop(0.5, 'rgba(255, 140, 0, 0.35)');
      lanternGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = lanternGlow;
      ctx.fillRect(130, 250, 260, 260);

      ctx.fillStyle = '#05070a';
      ctx.fillRect(256, 330, 8, 182);
      ctx.beginPath();
      ctx.arc(260, 330, 14, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // 4. STUDIO / VELVET / THUNDERDOME: Modern dusk skyline with architectural glow
      const skyGrad = ctx.createLinearGradient(0, 0, 0, h);
      skyGrad.addColorStop(0, '#0d1020');
      skyGrad.addColorStop(0.6, '#18243c');
      skyGrad.addColorStop(1, '#2b3b5c');
      ctx.fillStyle = skyGrad;
      ctx.fillRect(0, 0, w, h);

      ctx.fillStyle = '#090d18';
      for (let i = 0; i < 8; i++) {
        const bx = i * 65;
        const bw = 55;
        const bh = 180 + ((i * 47) % 150);
        ctx.fillRect(bx, h - bh, bw, bh);

        ctx.fillStyle = 'rgba(255, 230, 150, 0.45)';
        for (let wy = h - bh + 15; wy < h - 20; wy += 20) {
          for (let wx = bx + 8; wx < bx + bw - 8; wx += 14) {
            ctx.fillRect(wx, wy, 5, 8);
          }
        }
        ctx.fillStyle = '#090d18';
      }
    }
  }

  /**
   * Dispatches theme-specific 3D decorative room and tabletop prop generation.
   * @param {BABYLON.Scene} scene
   * @param {Object} BABYLON
   * @param {BABYLON.TransformNode} rootNode
   * @param {Object} theme
   * @param {Object} dims
   * @returns {Array<BABYLON.Mesh|BABYLON.TransformNode|BABYLON.Light>}
   */
  static buildProps(scene, BABYLON, rootNode, theme, dims) {
    if (!scene || !BABYLON || !theme || theme.id === 'none') return [];
    switch (theme.id) {
      case 'pub':
        return this.buildPubProps(scene, BABYLON, rootNode, dims);
      case 'vegas':
        return this.buildVegasProps(scene, BABYLON, rootNode, dims);
      case 'cabin':
        return this.buildCabinProps(scene, BABYLON, rootNode, dims);
      case 'velvet':
        return this.buildVelvetProps(scene, BABYLON, rootNode, dims);
      case 'thunderdome':
        return this.buildThunderdomeProps(scene, BABYLON, rootNode, dims);
      case 'studio':
        return this.buildStudioProps(scene, BABYLON, rootNode, dims);
      default:
        return [];
    }
  }

  /**
   * Builds Old English Pub thematic props: brick fireplace with glowing embers,
   * frosted pint tankards with foam heads on wooden coasters, and tavern wall sconces.
   */
  static buildPubProps(scene, BABYLON, rootNode, dims) {
    const props = [];
    const { tableW, tableD, tableSurfaceY, floorY, roomW, roomD } = dims;

    // --- 1. Tavern Brick Fireplace on Back Wall ---
    const fireplaceRoot = new BABYLON.TransformNode('pubFireplaceRoot', scene);
    fireplaceRoot.position.set(-roomW * 0.28, floorY, roomD / 2 - 14);
    fireplaceRoot.parent = rootNode;
    props.push(fireplaceRoot);

    const brickMat = new BABYLON.PBRMaterial('matPubFireplaceBrick', scene);
    brickMat.albedoColor = new BABYLON.Color3(0.36, 0.16, 0.10);
    brickMat.roughness = 0.85;

    const mantelMat = new BABYLON.PBRMaterial('matPubFireplaceMantel', scene);
    mantelMat.albedoColor = new BABYLON.Color3(0.18, 0.11, 0.06);
    mantelMat.roughness = 0.45;

    // Stone hearth base
    const hearth = BABYLON.MeshBuilder.CreateBox('pubFireHearth', { width: 70, height: 6, depth: 32 }, scene);
    hearth.position.set(0, 3, 0);
    hearth.parent = fireplaceRoot;
    hearth.material = brickMat;
    hearth.isPickable = false;
    props.push(hearth);

    // Left and right brick jambs / columns
    [-24, 24].forEach((jx, idx) => {
      const col = BABYLON.MeshBuilder.CreateBox(`pubFireCol_${idx}`, { width: 14, height: 90, depth: 22 }, scene);
      col.position.set(jx, 48, 0);
      col.parent = fireplaceRoot;
      col.material = brickMat;
      col.isPickable = false;
      props.push(col);
    });

    // Fireplace lintel / arch
    const lintel = BABYLON.MeshBuilder.CreateBox('pubFireLintel', { width: 62, height: 16, depth: 22 }, scene);
    lintel.position.set(0, 85, 0);
    lintel.parent = fireplaceRoot;
    lintel.material = brickMat;
    lintel.isPickable = false;
    props.push(lintel);

    // Rustic oak mantel shelf
    const mantel = BABYLON.MeshBuilder.CreateBox('pubFireMantel', { width: 84, height: 8, depth: 28 }, scene);
    mantel.position.set(0, 97, 2);
    mantel.parent = fireplaceRoot;
    mantel.material = mantelMat;
    mantel.isPickable = false;
    props.push(mantel);

    // Dark firebox cavity
    const fireboxMat = new BABYLON.PBRMaterial('matPubFirebox', scene);
    fireboxMat.albedoColor = new BABYLON.Color3(0.05, 0.04, 0.04);
    fireboxMat.roughness = 0.95;
    const firebox = BABYLON.MeshBuilder.CreateBox('pubFirebox', { width: 34, height: 64, depth: 16 }, scene);
    firebox.position.set(0, 35, -2);
    firebox.parent = fireplaceRoot;
    firebox.material = fireboxMat;
    firebox.isPickable = false;
    props.push(firebox);

    // Glowing embers / fire plane
    const fireMat = new BABYLON.PBRMaterial('matPubFireEmbers', scene);
    fireMat.emissiveColor = new BABYLON.Color3(1.0, 0.52, 0.12);
    fireMat.emissiveIntensity = 2.4;
    fireMat.backFaceCulling = false;
    const doubleSide = (BABYLON.Mesh && BABYLON.Mesh.DOUBLESIDE != null) ? BABYLON.Mesh.DOUBLESIDE : 2;
    const embers = BABYLON.MeshBuilder.CreatePlane('pubFireEmbers', { width: 26, height: 26, sideOrientation: doubleSide }, scene);
    embers.position.set(0, 18, 5);
    embers.parent = fireplaceRoot;
    embers.material = fireMat;
    embers.isPickable = false;
    props.push(embers);

    // Fireplace warm flicker point light
    const fireLight = new BABYLON.PointLight('pubFireLight', new BABYLON.Vector3(0, 22, 10), scene);
    fireLight.diffuse = new BABYLON.Color3(1.0, 0.55, 0.15);
    fireLight.intensity = 1.35;
    fireLight.range = 190;
    fireLight.parent = fireplaceRoot;
    props.push(fireLight);

    // --- 2. Frosted Pint Tankards on Table Coasters ---
    const coasterMat = new BABYLON.PBRMaterial('matPubCoaster', scene);
    coasterMat.albedoColor = new BABYLON.Color3(0.72, 0.58, 0.42);
    coasterMat.roughness = 0.6;

    const tankardMat = new BABYLON.PBRMaterial('matPubGlassTankard', scene);
    tankardMat.albedoColor = new BABYLON.Color3(0.85, 0.55, 0.15);
    tankardMat.alpha = 0.82;
    tankardMat.roughness = 0.2;
    tankardMat.metallic = 0.1;

    const foamMat = new BABYLON.PBRMaterial('matPubFoamHead', scene);
    foamMat.albedoColor = new BABYLON.Color3(0.96, 0.94, 0.88);
    foamMat.roughness = 0.7;

    const mugCoords = [
      [-tableW * 0.38, -tableD * 0.35],
      [tableW * 0.38, tableD * 0.35]
    ];

    mugCoords.forEach(([mx, mz], idx) => {
      const mugRoot = new BABYLON.TransformNode(`pubMugRoot_${idx}`, scene);
      mugRoot.position.set(mx, tableSurfaceY, mz);
      mugRoot.parent = rootNode;
      props.push(mugRoot);

      // Coaster
      const coaster = BABYLON.MeshBuilder.CreateCylinder(`pubCoaster_${idx}`, { diameter: 10, height: 0.6, tessellation: 24 }, scene);
      coaster.position.set(0, 0.3, 0);
      coaster.parent = mugRoot;
      coaster.material = coasterMat;
      coaster.isPickable = false;
      props.push(coaster);

      // Pint Tankard Glass Body
      const body = BABYLON.MeshBuilder.CreateCylinder(`pubTankard_${idx}`, { diameterTop: 5.2, diameterBottom: 4.6, height: 10.0, tessellation: 20 }, scene);
      body.position.set(0, 5.6, 0);
      body.parent = mugRoot;
      body.material = tankardMat;
      body.isPickable = false;
      props.push(body);

      // Creamy Froth Foam Head
      const foam = BABYLON.MeshBuilder.CreateCylinder(`pubFoam_${idx}`, { diameter: 5.3, height: 2.2, tessellation: 20 }, scene);
      foam.position.set(0, 10.6, 0);
      foam.parent = mugRoot;
      foam.material = foamMat;
      foam.isPickable = false;
      props.push(foam);

      // Mug Handle
      const handle = BABYLON.MeshBuilder.CreateBox(`pubHandle_${idx}`, { width: 1.2, height: 5.8, depth: 3.2 }, scene);
      handle.position.set(3.0, 5.8, 0);
      handle.parent = mugRoot;
      handle.material = tankardMat;
      handle.isPickable = false;
      props.push(handle);
    });

    // --- 3. Wall Sconces on Left & Right Walls ---
    const sconceMat = new BABYLON.PBRMaterial('matPubSconce', scene);
    sconceMat.albedoColor = new BABYLON.Color3(0.24, 0.18, 0.12);
    sconceMat.metallic = 0.8;
    sconceMat.roughness = 0.3;

    const bulbMat = new BABYLON.PBRMaterial('matPubSconceBulb', scene);
    bulbMat.emissiveColor = new BABYLON.Color3(1.0, 0.78, 0.38);
    bulbMat.emissiveIntensity = 2.0;

    [-roomW / 2 + 3.0].forEach((sx, idx) => {
      const sconceRoot = new BABYLON.TransformNode(`pubSconceRoot_${idx}`, scene);
      sconceRoot.position.set(sx, floorY + 200, -roomD * 0.15);
      sconceRoot.parent = rootNode;
      props.push(sconceRoot);

      const bracket = BABYLON.MeshBuilder.CreateBox(`pubSconceBracket_${idx}`, { width: 2.4, height: 14, depth: 8 }, scene);
      bracket.parent = sconceRoot;
      bracket.material = sconceMat;
      bracket.isPickable = false;
      props.push(bracket);

      const shade = BABYLON.MeshBuilder.CreateCylinder(`pubSconceShade_${idx}`, { diameterTop: 6, diameterBottom: 4, height: 8, tessellation: 16 }, scene);
      shade.position.set(4, 5, 0);
      shade.parent = sconceRoot;
      shade.material = bulbMat;
      shade.isPickable = false;
      props.push(shade);
    });

    return props;
  }

  /**
   * Builds Vegas High Roller Suite thematic props: stacked clay casino chips,
   * crystal decanter & whiskey tumbler, and luxury gold crown moldings.
   */
  static buildVegasProps(scene, BABYLON, rootNode, dims) {
    const props = [];
    const { tableW, tableD, tableSurfaceY, floorY, roomW, roomD } = dims;

    // --- 1. Casino Clay Chip Stacks ---
    const chipRoot = new BABYLON.TransformNode('vegasChipsRoot', scene);
    chipRoot.position.set(-tableW * 0.38, tableSurfaceY, -tableD * 0.34);
    chipRoot.parent = rootNode;
    props.push(chipRoot);

    const redChipMat = new BABYLON.PBRMaterial('matVegasRedChip', scene);
    redChipMat.albedoColor = new BABYLON.Color3(0.78, 0.10, 0.12);
    redChipMat.roughness = 0.35;

    const blueChipMat = new BABYLON.PBRMaterial('matVegasBlueChip', scene);
    blueChipMat.albedoColor = new BABYLON.Color3(0.12, 0.28, 0.75);
    blueChipMat.roughness = 0.35;

    const goldChipMat = new BABYLON.PBRMaterial('matVegasGoldChip', scene);
    goldChipMat.albedoColor = new BABYLON.Color3(0.92, 0.76, 0.22);
    goldChipMat.metallic = 0.75;
    goldChipMat.roughness = 0.25;

    const blackChipMat = new BABYLON.PBRMaterial('matVegasBlackChip', scene);
    blackChipMat.albedoColor = new BABYLON.Color3(0.12, 0.12, 0.14);
    blackChipMat.roughness = 0.4;

    const chipConfigs = [
      { name: 'stackRed', mat: redChipMat, h: 7.2, x: 0, z: 0 },
      { name: 'stackBlue', mat: blueChipMat, h: 9.6, x: 5.5, z: 1.5 },
      { name: 'stackGold', mat: goldChipMat, h: 6.0, x: -3.5, z: 4.5 },
      { name: 'stackBlack', mat: blackChipMat, h: 8.4, x: 2.5, z: 6.0 }
    ];

    chipConfigs.forEach(c => {
      const stack = BABYLON.MeshBuilder.CreateCylinder(`vegas_${c.name}`, { diameter: 4.8, height: c.h, tessellation: 24 }, scene);
      stack.position.set(c.x, c.h / 2, c.z);
      stack.parent = chipRoot;
      stack.material = c.mat;
      stack.isPickable = false;
      props.push(stack);
    });

    // --- 2. Crystal Decanter & Whiskey Glass ---
    const drinkRoot = new BABYLON.TransformNode('vegasDrinkRoot', scene);
    drinkRoot.position.set(tableW * 0.38, tableSurfaceY, tableD * 0.34);
    drinkRoot.parent = rootNode;
    props.push(drinkRoot);

    const crystalMat = new BABYLON.PBRMaterial('matVegasCrystal', scene);
    crystalMat.albedoColor = new BABYLON.Color3(0.92, 0.95, 1.0);
    crystalMat.alpha = 0.45;
    crystalMat.roughness = 0.08;
    crystalMat.metallic = 0.15;

    const whiskeyMat = new BABYLON.PBRMaterial('matVegasWhiskey', scene);
    whiskeyMat.albedoColor = new BABYLON.Color3(0.88, 0.52, 0.10);
    whiskeyMat.alpha = 0.85;
    whiskeyMat.roughness = 0.2;

    // Decanter body
    const decanter = BABYLON.MeshBuilder.CreateCylinder('vegasDecanterBody', { diameterTop: 4.0, diameterBottom: 7.0, height: 12.0, tessellation: 20 }, scene);
    decanter.position.set(0, 6.0, 0);
    decanter.parent = drinkRoot;
    decanter.material = crystalMat;
    decanter.isPickable = false;
    props.push(decanter);

    // Whiskey in decanter
    const liquor = BABYLON.MeshBuilder.CreateCylinder('vegasLiquor', { diameter: 5.8, height: 6.0, tessellation: 18 }, scene);
    liquor.position.set(0, 3.4, 0);
    liquor.parent = drinkRoot;
    liquor.material = whiskeyMat;
    liquor.isPickable = false;
    props.push(liquor);

    // Decanter crystal stopper
    const stopper = BABYLON.MeshBuilder.CreateSphere('vegasStopper', { diameter: 4.2 }, scene);
    stopper.position.set(0, 13.5, 0);
    stopper.parent = drinkRoot;
    stopper.material = crystalMat;
    stopper.isPickable = false;
    props.push(stopper);

    // Whiskey tumbler glass
    const tumbler = BABYLON.MeshBuilder.CreateCylinder('vegasTumbler', { diameter: 4.6, height: 5.5, tessellation: 20 }, scene);
    tumbler.position.set(7.5, 2.75, 2.0);
    tumbler.parent = drinkRoot;
    tumbler.material = crystalMat;
    tumbler.isPickable = false;
    props.push(tumbler);

    // Gold VIP wall molding trims
    const goldMat = new BABYLON.PBRMaterial('matVegasGoldTrim', scene);
    goldMat.albedoColor = new BABYLON.Color3(0.90, 0.76, 0.28);
    goldMat.metallic = 0.92;
    goldMat.roughness = 0.22;

    const goldTrim = BABYLON.MeshBuilder.CreateBox('vegasGoldTrimNorth', { width: roomW * 0.9, height: 2.2, depth: 1.8 }, scene);
    goldTrim.position.set(0, floorY + 340, roomD / 2 - 2.0);
    goldTrim.parent = rootNode;
    goldTrim.material = goldMat;
    goldTrim.isPickable = false;
    props.push(goldTrim);

    return props;
  }

  /**
   * Builds Mountain Lodge thematic props: stone hearth fireplace,
   * steaming stoneware coffee mugs on wood-slice coasters.
   */
  static buildCabinProps(scene, BABYLON, rootNode, dims) {
    const props = [];
    const { tableW, tableD, tableSurfaceY, floorY, roomW, roomD } = dims;

    // --- 1. Rustic Stone Hearth Fireplace ---
    const hearthRoot = new BABYLON.TransformNode('cabinHearthRoot', scene);
    hearthRoot.position.set(roomW * 0.28, floorY, roomD / 2 - 14);
    hearthRoot.parent = rootNode;
    props.push(hearthRoot);

    const stoneMat = new BABYLON.PBRMaterial('matCabinStone', scene);
    stoneMat.albedoColor = new BABYLON.Color3(0.32, 0.29, 0.26);
    stoneMat.roughness = 0.9;

    const pineMat = new BABYLON.PBRMaterial('matCabinPineLog', scene);
    pineMat.albedoColor = new BABYLON.Color3(0.24, 0.15, 0.08);
    pineMat.roughness = 0.65;

    // Heavy foundation hearthstone
    const hearthBase = BABYLON.MeshBuilder.CreateBox('cabinHearthBase', { width: 76, height: 6, depth: 34 }, scene);
    hearthBase.position.set(0, 3, 0);
    hearthBase.parent = hearthRoot;
    hearthBase.material = stoneMat;
    hearthBase.isPickable = false;
    props.push(hearthBase);

    // Stone pillars
    [-26, 26].forEach((px, idx) => {
      const p = BABYLON.MeshBuilder.CreateBox(`cabinStonePillar_${idx}`, { width: 16, height: 96, depth: 24 }, scene);
      p.position.set(px, 51, 0);
      p.parent = hearthRoot;
      p.material = stoneMat;
      p.isPickable = false;
      props.push(p);
    });

    // Massive pine timber mantel
    const timberMantel = BABYLON.MeshBuilder.CreateBox('cabinTimberMantel', { width: 92, height: 12, depth: 30 }, scene);
    timberMantel.position.set(0, 103, 2);
    timberMantel.parent = hearthRoot;
    timberMantel.material = pineMat;
    timberMantel.isPickable = false;
    props.push(timberMantel);

    // Burning hearth embers
    const fireMat = new BABYLON.PBRMaterial('matCabinFire', scene);
    fireMat.emissiveColor = new BABYLON.Color3(1.0, 0.58, 0.14);
    fireMat.emissiveIntensity = 2.5;
    fireMat.backFaceCulling = false;

    const doubleSide = (BABYLON.Mesh && BABYLON.Mesh.DOUBLESIDE != null) ? BABYLON.Mesh.DOUBLESIDE : 2;
    const embers = BABYLON.MeshBuilder.CreatePlane('cabinEmbers', { width: 30, height: 30, sideOrientation: doubleSide }, scene);
    embers.position.set(0, 20, 6);
    embers.parent = hearthRoot;
    embers.material = fireMat;
    embers.isPickable = false;
    props.push(embers);

    // Fire point light
    const fireLight = new BABYLON.PointLight('cabinFireLight', new BABYLON.Vector3(0, 26, 12), scene);
    fireLight.diffuse = new BABYLON.Color3(1.0, 0.60, 0.18);
    fireLight.intensity = 1.4;
    fireLight.range = 195;
    fireLight.parent = hearthRoot;
    props.push(fireLight);

    // --- 2. Steaming Stoneware Coffee Mugs on Wood-Slice Coasters ---
    const mugMat = new BABYLON.PBRMaterial('matCabinMug', scene);
    mugMat.albedoColor = new BABYLON.Color3(0.28, 0.22, 0.18);
    mugMat.roughness = 0.55;

    const coffeeMat = new BABYLON.PBRMaterial('matCabinCoffee', scene);
    coffeeMat.albedoColor = new BABYLON.Color3(0.10, 0.06, 0.04);
    coffeeMat.roughness = 0.3;

    const birchMat = new BABYLON.PBRMaterial('matCabinBirchCoaster', scene);
    birchMat.albedoColor = new BABYLON.Color3(0.55, 0.42, 0.28);
    birchMat.roughness = 0.7;

    const mugCoords = [
      [-tableW * 0.38, tableD * 0.34],
      [tableW * 0.38, -tableD * 0.34]
    ];

    mugCoords.forEach(([mx, mz], idx) => {
      const mugRoot = new BABYLON.TransformNode(`cabinMugRoot_${idx}`, scene);
      mugRoot.position.set(mx, tableSurfaceY, mz);
      mugRoot.parent = rootNode;
      props.push(mugRoot);

      // Wood slice coaster
      const coaster = BABYLON.MeshBuilder.CreateCylinder(`cabinCoaster_${idx}`, { diameter: 9.6, height: 0.7, tessellation: 20 }, scene);
      coaster.position.set(0, 0.35, 0);
      coaster.parent = mugRoot;
      coaster.material = birchMat;
      coaster.isPickable = false;
      props.push(coaster);

      // Stoneware mug
      const mug = BABYLON.MeshBuilder.CreateCylinder(`cabinMug_${idx}`, { diameter: 5.4, height: 7.5, tessellation: 20 }, scene);
      mug.position.set(0, 4.2, 0);
      mug.parent = mugRoot;
      mug.material = mugMat;
      mug.isPickable = false;
      props.push(mug);

      // Coffee surface
      const coffee = BABYLON.MeshBuilder.CreateCylinder(`cabinCoffee_${idx}`, { diameter: 4.8, height: 0.5, tessellation: 18 }, scene);
      coffee.position.set(0, 7.5, 0);
      coffee.parent = mugRoot;
      coffee.material = coffeeMat;
      coffee.isPickable = false;
      props.push(coffee);

      // Handle
      const handle = BABYLON.MeshBuilder.CreateBox(`cabinHandle_${idx}`, { width: 1.2, height: 4.8, depth: 2.8 }, scene);
      handle.position.set(3.0, 4.4, 0);
      handle.parent = mugRoot;
      handle.material = mugMat;
      handle.isPickable = false;
      props.push(handle);
    });

    return props;
  }

  /**
   * Builds Velvet Speakeasy Lounge thematic props: art-deco cocktail glass,
   * emerald brass banker table lamp.
   */
  static buildVelvetProps(scene, BABYLON, rootNode, dims) {
    const props = [];
    const { tableW, tableD, tableSurfaceY } = dims;

    // --- 1. Art-Deco Martini / Cocktail Glass ---
    const cocktailRoot = new BABYLON.TransformNode('velvetCocktailRoot', scene);
    cocktailRoot.position.set(-tableW * 0.38, tableSurfaceY, -tableD * 0.34);
    cocktailRoot.parent = rootNode;
    props.push(cocktailRoot);

    const glassMat = new BABYLON.PBRMaterial('matVelvetGlass', scene);
    glassMat.albedoColor = new BABYLON.Color3(0.90, 0.95, 0.98);
    glassMat.alpha = 0.5;
    glassMat.roughness = 0.1;

    const oliveMat = new BABYLON.PBRMaterial('matVelvetOlive', scene);
    oliveMat.albedoColor = new BABYLON.Color3(0.35, 0.45, 0.15);
    oliveMat.roughness = 0.4;

    // Glass base & stem
    const gBase = BABYLON.MeshBuilder.CreateCylinder('velvetGlassBase', { diameter: 5.0, height: 0.4, tessellation: 20 }, scene);
    gBase.position.set(0, 0.2, 0);
    gBase.parent = cocktailRoot;
    gBase.material = glassMat;
    gBase.isPickable = false;
    props.push(gBase);

    const gStem = BABYLON.MeshBuilder.CreateCylinder('velvetGlassStem', { diameter: 0.7, height: 5.5, tessellation: 14 }, scene);
    gStem.position.set(0, 3.1, 0);
    gStem.parent = cocktailRoot;
    gStem.material = glassMat;
    gStem.isPickable = false;
    props.push(gStem);

    // Inverted conical martini cup
    const gCup = BABYLON.MeshBuilder.CreateCylinder('velvetGlassCup', { diameterTop: 6.8, diameterBottom: 1.2, height: 4.2, tessellation: 20 }, scene);
    gCup.position.set(0, 7.8, 0);
    gCup.parent = cocktailRoot;
    gCup.material = glassMat;
    gCup.isPickable = false;
    props.push(gCup);

    // Green cocktail olive
    const olive = BABYLON.MeshBuilder.CreateSphere('velvetOlive', { diameter: 2.0 }, scene);
    olive.position.set(0.6, 7.2, 0);
    olive.parent = cocktailRoot;
    olive.material = oliveMat;
    olive.isPickable = false;
    props.push(olive);

    // --- 2. Art-Deco Banker / Emerald Table Lamp ---
    const lampRoot = new BABYLON.TransformNode('velvetLampRoot', scene);
    lampRoot.position.set(tableW * 0.38, tableSurfaceY, tableD * 0.34);
    lampRoot.parent = rootNode;
    props.push(lampRoot);

    const brassMat = new BABYLON.PBRMaterial('matVelvetBrass', scene);
    brassMat.albedoColor = new BABYLON.Color3(0.88, 0.74, 0.28);
    brassMat.metallic = 0.95;
    brassMat.roughness = 0.2;

    const shadeMat = new BABYLON.PBRMaterial('matVelvetLampShade', scene);
    shadeMat.albedoColor = new BABYLON.Color3(0.08, 0.48, 0.28);
    shadeMat.emissiveColor = new BABYLON.Color3(0.05, 0.25, 0.14);
    shadeMat.emissiveIntensity = 1.2;
    shadeMat.roughness = 0.25;

    // Brass base
    const lBase = BABYLON.MeshBuilder.CreateCylinder('velvetLampBase', { diameter: 7.0, height: 1.2, tessellation: 24 }, scene);
    lBase.position.set(0, 0.6, 0);
    lBase.parent = lampRoot;
    lBase.material = brassMat;
    lBase.isPickable = false;
    props.push(lBase);

    // Brass column neck
    const lNeck = BABYLON.MeshBuilder.CreateCylinder('velvetLampNeck', { diameter: 1.2, height: 12.0, tessellation: 16 }, scene);
    lNeck.position.set(0, 6.8, 0);
    lNeck.parent = lampRoot;
    lNeck.material = brassMat;
    lNeck.isPickable = false;
    props.push(lNeck);

    // Emerald shade
    const lShade = BABYLON.MeshBuilder.CreateBox('velvetLampShade', { width: 8.8, height: 4.2, depth: 5.6 }, scene);
    lShade.position.set(0, 13.5, 0);
    lShade.parent = lampRoot;
    lShade.material = shadeMat;
    lShade.isPickable = false;
    props.push(lShade);

    // Warm table lamp fill light
    const lampLight = new BABYLON.PointLight('velvetLampLight', new BABYLON.Vector3(0, 12, 0), scene);
    lampLight.diffuse = new BABYLON.Color3(1.0, 0.88, 0.62);
    lampLight.intensity = 0.85;
    lampLight.range = 85;
    lampLight.parent = lampRoot;
    props.push(lampLight);

    return props;
  }

  /**
   * Builds Thunderdome / Industrial thematic props: exposed glowing neon strips along wall/ceiling corners,
   * heavy steel fuel canister on table corner, and ventilation exhaust duct.
   */
  static buildThunderdomeProps(scene, BABYLON, rootNode, dims) {
    const props = [];
    const { tableW, tableD, tableSurfaceY, ceilingY, roomW, roomD } = dims;

    // --- 1. Glowing Neon Strip Tubes Along Wall/Ceiling Corners ---
    const cyanNeonMat = new BABYLON.PBRMaterial('matThunderNeonCyan', scene);
    cyanNeonMat.emissiveColor = new BABYLON.Color3(0.0, 0.95, 1.0);
    cyanNeonMat.emissiveIntensity = 3.0;

    const orangeNeonMat = new BABYLON.PBRMaterial('matThunderNeonOrange', scene);
    orangeNeonMat.emissiveColor = new BABYLON.Color3(1.0, 0.45, 0.0);
    orangeNeonMat.emissiveIntensity = 3.0;

    // Back wall top neon strip (Cyan)
    const neonNorth = BABYLON.MeshBuilder.CreateCylinder('thunderNeonNorth', { diameter: 3.2, height: roomW * 0.85, tessellation: 16 }, scene);
    neonNorth.rotation.z = Math.PI / 2;
    neonNorth.position.set(0, ceilingY - 18, roomD / 2 - 10);
    neonNorth.parent = rootNode;
    neonNorth.material = cyanNeonMat;
    neonNorth.isPickable = false;
    props.push(neonNorth);

    // Left wall top neon strip (Orange)
    const neonWest = BABYLON.MeshBuilder.CreateCylinder('thunderNeonWest', { diameter: 3.2, height: roomD * 0.85, tessellation: 16 }, scene);
    neonWest.rotation.x = Math.PI / 2;
    neonWest.position.set(-roomW / 2 + 10, ceilingY - 18, 0);
    neonWest.parent = rootNode;
    neonWest.material = orangeNeonMat;
    neonWest.isPickable = false;
    props.push(neonWest);

    // --- 2. Industrial Steel Canister on Table Corner ---
    const canisterRoot = new BABYLON.TransformNode('thunderCanisterRoot', scene);
    canisterRoot.position.set(tableW * 0.38, tableSurfaceY, -tableD * 0.34);
    canisterRoot.parent = rootNode;
    props.push(canisterRoot);

    const steelMat = new BABYLON.PBRMaterial('matThunderSteel', scene);
    steelMat.albedoColor = new BABYLON.Color3(0.25, 0.28, 0.32);
    steelMat.metallic = 0.95;
    steelMat.roughness = 0.25;

    const bronzeMat = new BABYLON.PBRMaterial('matThunderBronze', scene);
    bronzeMat.albedoColor = new BABYLON.Color3(0.72, 0.52, 0.24);
    bronzeMat.metallic = 0.85;
    bronzeMat.roughness = 0.3;

    // Canister main body
    const canBody = BABYLON.MeshBuilder.CreateCylinder('thunderCanBody', { diameter: 5.6, height: 9.5, tessellation: 20 }, scene);
    canBody.position.set(0, 4.75, 0);
    canBody.parent = canisterRoot;
    canBody.material = steelMat;
    canBody.isPickable = false;
    props.push(canBody);

    // Pressure cap / valve
    const canCap = BABYLON.MeshBuilder.CreateCylinder('thunderCanCap', { diameter: 2.4, height: 2.2, tessellation: 16 }, scene);
    canCap.position.set(0, 10.2, 0);
    canCap.parent = canisterRoot;
    canCap.material = bronzeMat;
    canCap.isPickable = false;
    props.push(canCap);

    // Industrial wall vent duct on back wall
    const ventMat = new BABYLON.PBRMaterial('matThunderVent', scene);
    ventMat.albedoColor = new BABYLON.Color3(0.18, 0.20, 0.22);
    ventMat.metallic = 0.8;
    ventMat.roughness = 0.4;

    const vent = BABYLON.MeshBuilder.CreateBox('thunderVentGrate', { width: 36, height: 36, depth: 6 }, scene);
    vent.position.set(roomW * 0.28, ceilingY - 90, roomD / 2 - 3.0);
    vent.parent = rootNode;
    vent.material = ventMat;
    vent.isPickable = false;
    props.push(vent);

    return props;
  }

  /**
   * Builds Nordic Minimalist Studio thematic props: potted succulent plant,
   * modern stone coaster and ceramic cup, and diffuse daylight skylight fixture.
   */
  static buildStudioProps(scene, BABYLON, rootNode, dims) {
    const props = [];
    const { tableW, tableD, tableSurfaceY, ceilingY } = dims;

    // --- 1. Potted Succulent Plant on Table Periphery ---
    const plantRoot = new BABYLON.TransformNode('studioPlantRoot', scene);
    plantRoot.position.set(tableW * 0.38, tableSurfaceY, tableD * 0.34);
    plantRoot.parent = rootNode;
    props.push(plantRoot);

    const potMat = new BABYLON.PBRMaterial('matStudioPot', scene);
    potMat.albedoColor = new BABYLON.Color3(0.92, 0.90, 0.86);
    potMat.roughness = 0.65;

    const soilMat = new BABYLON.PBRMaterial('matStudioSoil', scene);
    soilMat.albedoColor = new BABYLON.Color3(0.16, 0.12, 0.08);
    soilMat.roughness = 0.9;

    const leafMat = new BABYLON.PBRMaterial('matStudioSucculent', scene);
    leafMat.albedoColor = new BABYLON.Color3(0.35, 0.65, 0.42);
    leafMat.roughness = 0.45;

    // Planter pot
    const pot = BABYLON.MeshBuilder.CreateCylinder('studioPlanterPot', { diameter: 6.2, height: 6.5, tessellation: 24 }, scene);
    pot.position.set(0, 3.25, 0);
    pot.parent = plantRoot;
    pot.material = potMat;
    pot.isPickable = false;
    props.push(pot);

    // Soil surface
    const soil = BABYLON.MeshBuilder.CreateCylinder('studioSoil', { diameter: 5.6, height: 0.6, tessellation: 20 }, scene);
    soil.position.set(0, 6.2, 0);
    soil.parent = plantRoot;
    soil.material = soilMat;
    soil.isPickable = false;
    props.push(soil);

    // Succulent rosettes
    const rosettePositions = [
      [0, 7.4, 0, 1.8],
      [-1.4, 7.0, -1.2, 1.4],
      [1.4, 7.0, -1.2, 1.4],
      [0, 7.0, 1.5, 1.4]
    ];
    rosettePositions.forEach(([rx, ry, rz, rRad], idx) => {
      const leaf = BABYLON.MeshBuilder.CreateSphere(`studioLeaf_${idx}`, { diameter: rRad * 2 }, scene);
      leaf.position.set(rx, ry, rz);
      leaf.parent = plantRoot;
      leaf.material = leafMat;
      leaf.isPickable = false;
      props.push(leaf);
    });

    // --- 2. Minimalist Ceramic Cup & Stone Coaster ---
    const cupRoot = new BABYLON.TransformNode('studioCupRoot', scene);
    cupRoot.position.set(-tableW * 0.38, tableSurfaceY, -tableD * 0.34);
    cupRoot.parent = rootNode;
    props.push(cupRoot);

    const stoneMat = new BABYLON.PBRMaterial('matStudioStoneCoaster', scene);
    stoneMat.albedoColor = new BABYLON.Color3(0.48, 0.48, 0.50);
    stoneMat.roughness = 0.7;

    const coaster = BABYLON.MeshBuilder.CreateCylinder('studioCoaster', { diameter: 8.5, height: 0.5, tessellation: 24 }, scene);
    coaster.position.set(0, 0.25, 0);
    coaster.parent = cupRoot;
    coaster.material = stoneMat;
    coaster.isPickable = false;
    props.push(coaster);

    const cup = BABYLON.MeshBuilder.CreateCylinder('studioCup', { diameter: 4.8, height: 6.8, tessellation: 20 }, scene);
    cup.position.set(0, 3.8, 0);
    cup.parent = cupRoot;
    cup.material = potMat;
    cup.isPickable = false;
    props.push(cup);

    // --- 3. Daylight Diffuse Skylight Ceiling Panel ---
    const skyMat = new BABYLON.PBRMaterial('matStudioSkylight', scene);
    skyMat.emissiveColor = new BABYLON.Color3(0.95, 0.98, 1.0);
    skyMat.emissiveIntensity = 1.6;

    const skylight = BABYLON.MeshBuilder.CreateBox('studioSkylightFixture', { width: 90, height: 2.4, depth: 70 }, scene);
    skylight.position.set(0, ceilingY - 12, 0);
    skylight.parent = rootNode;
    skylight.material = skyMat;
    skylight.isPickable = false;
    props.push(skylight);

    return props;
  }

  /**
   * Cleans up and disposes all room nodes, meshes, textures, materials, and lights.
   * @param {Object} roomData
   */
  static disposeRoom(roomData) {
    if (!roomData) return;
    try {
      if (roomData.props && Array.isArray(roomData.props)) {
        roomData.props.forEach(p => {
          try {
            if (p && typeof p.dispose === 'function') {
              p.dispose(false, true);
            }
          } catch (_) {}
        });
      }
      if (roomData.lights && Array.isArray(roomData.lights)) {
        roomData.lights.forEach(l => {
          try { l.dispose(); } catch (_) {}
        });
      }
      if (roomData.rootNode) {
        try { roomData.rootNode.dispose(false, true); } catch (_) {}
      }
    } catch (e) {
      console.warn('[RoomCatalog3D] Error disposing room resources:', e);
    }
  }
}
